/**
 * AI 配置
 * 管理 AI 相关的全局配置
 */

import fs from 'fs-extra';
import path from 'path';
import { logger } from '../utils/logger';

export interface AIConfig {
  // AI 模式
  mode: 'auto' | 'manual' | 'hybrid' | 'memory-enhanced';
  
  // Memory 系统配置
  memory: {
    enabled: boolean;
    userId?: string;
    projectId?: string;
    vectorStore?: {
      provider: string;
      config: any;
    };
  };
  
  // Agent 分配策略
  agentAllocation: {
    preferMemory: boolean; // 优先使用记忆推荐
    minConfidence: number; // 最小置信度（0-1）
    fallbackToRules: boolean; // 记忆失败时回退到规则
  };
  
  // 默认设置
  defaults: {
    enableForNewTasks: boolean; // 新任务自动启用 AI
    enableForPRD: boolean; // PRD 生成时启用 AI
    autoAssignAgents: boolean; // 自动分配 Agent
  };
}

export class AIConfigManager {
  private configPath: string;
  private config: AIConfig;
  
  constructor(workflowDir?: string) {
    const projectRoot = workflowDir || process.cwd();
    this.configPath = path.join(projectRoot, '.workflow', 'config', 'ai.json');
    this.config = this.loadConfig();
  }
  
  /**
   * 加载配置
   */
  private loadConfig(): AIConfig {
    if (fs.existsSync(this.configPath)) {
      try {
        return fs.readJSONSync(this.configPath);
      } catch (error) {
        logger.warn('Failed to load AI config, using defaults:', error);
      }
    }
    
    return this.getDefaultConfig();
  }
  
  /**
   * 获取默认配置
   */
  private getDefaultConfig(): AIConfig {
    return {
      mode: 'auto',
      memory: {
        enabled: false,
        userId: process.env.USER,
        projectId: undefined,
      },
      agentAllocation: {
        preferMemory: true,
        minConfidence: 0.7,
        fallbackToRules: true,
      },
      defaults: {
        enableForNewTasks: true,
        enableForPRD: true,
        autoAssignAgents: true,
      },
    };
  }
  
  /**
   * 保存配置
   */
  async saveConfig(): Promise<void> {
    await fs.ensureDir(path.dirname(this.configPath));
    await fs.writeJSON(this.configPath, this.config, { spaces: 2 });
    logger.info('AI config saved');
  }
  
  /**
   * 获取配置
   */
  getConfig(): AIConfig {
    return { ...this.config };
  }
  
  /**
   * 更新配置
   */
  async updateConfig(updates: Partial<AIConfig>): Promise<void> {
    this.config = {
      ...this.config,
      ...updates,
      memory: {
        ...this.config.memory,
        ...(updates.memory || {}),
      },
      agentAllocation: {
        ...this.config.agentAllocation,
        ...(updates.agentAllocation || {}),
      },
      defaults: {
        ...this.config.defaults,
        ...(updates.defaults || {}),
      },
    };
    
    await this.saveConfig();
  }
  
  /**
   * 启用 Memory 系统
   */
  async enableMemory(userId?: string, projectId?: string): Promise<void> {
    await this.updateConfig({
      mode: 'memory-enhanced',
      memory: {
        enabled: true,
        userId: userId || this.config.memory.userId,
        projectId: projectId || this.config.memory.projectId,
      },
    });
  }
  
  /**
   * 禁用 Memory 系统
   */
  async disableMemory(): Promise<void> {
    await this.updateConfig({
      mode: 'auto',
      memory: {
        ...this.config.memory,
        enabled: false,
      },
    });
  }
  
  /**
   * 设置 AI 模式
   */
  async setMode(mode: AIConfig['mode']): Promise<void> {
    await this.updateConfig({ mode });
  }
  
  /**
   * 是否应该为任务启用 AI
   */
  shouldEnableAIForTask(): boolean {
    return this.config.defaults.enableForNewTasks && this.config.mode !== 'manual';
  }
  
  /**
   * 是否应该为 PRD 启用 AI
   */
  shouldEnableAIForPRD(): boolean {
    return this.config.defaults.enableForPRD && this.config.mode !== 'manual';
  }
  
  /**
   * 是否应该自动分配 Agent
   */
  shouldAutoAssignAgents(): boolean {
    return this.config.defaults.autoAssignAgents && this.config.mode !== 'manual';
  }
}